// Build-version helpers for the unified Gposingway SPA.
//
// The legacy multi-SPA platform read the runtime platform version from www's /manifest at
// session start so cross-subdomain assets could share a cache key. The new SPA is a single
// deployable served from one origin - there's no cross-app coordination to do - so the
// version surface collapses to a Vite-time constant.
//
// `versionedUrl(path)` appends `?v=<APP_VERSION>` to a hand-coded static asset URL so the
// browser treats it as a fresh resource on every deploy. Vite's `/assets/*` outputs are
// already content-hashed, so this is only for files outside the build pipeline
// (e.g. /loporrit-mark-192.png, /theme/tokens.css).

// Vite injects an `env` property onto `import.meta` at build time, replacing
// `import.meta.env.VITE_APP_VERSION` with a string literal. We declare the surface locally
// rather than pulling in `vite/client` as a global type - that avoids forcing the rest of the
// SPA to depend on Vite's full ambient type set.
interface ImportMetaWithEnv {
    env?: { VITE_APP_VERSION?: string };
}

/**
 * Build version baked into the SPA bundle at build time. Falls back to "dev" when
 * `VITE_APP_VERSION` isn't set (`npm run dev` without an explicit env var).
 */
export const APP_VERSION: string = (() => {
    const meta = import.meta as unknown as ImportMetaWithEnv;
    const v = meta.env?.VITE_APP_VERSION;
    return v ? String(v) : 'dev';
})();

/**
 * Append `?v=<APP_VERSION>` to a static asset URL. Preserves any existing query string by
 * switching the separator from `?` to `&` when needed.
 *
 *   versionedUrl('/loporrit-mark-192.png')   → '/loporrit-mark-192.png?v=dev'
 *   versionedUrl('/api/foo?bar=1')           → '/api/foo?bar=1&v=dev'
 */
export function versionedUrl(path: string): string {
    if (!path) return path;
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}v=${encodeURIComponent(APP_VERSION)}`;
}
